// The agency's own space in the console (D-59, « 1 + 5 + 4 »; D-56 sign-in):
// « Mon agence » (design 1: the wallet and its top-up first, today, the
// riders), « Journée » (design 4: the riders' day and the cash to collect),
// « Commandes » (its orders, C18), « Compte » (design 5: the curve and the
// statement). It reads only its own agency (migration 0053) and never the
// overdraft amount (D-43).
import { useCallback, useEffect, useState } from 'react';
import { S } from '../lib/strings';
import { dayLabel, dec1, fmtN, hhmm, minutesBetween, rel, xaf } from '../lib/format';
import { avgMin, blocked, dayIn, firstDone, laneCash, longDay, netName, perHour, signedXaf, hm } from '../lib/agences';
import { telHref, waHref } from '../lib/phone';
import { applyTheme, readTheme, saveTheme, THEMES, type Theme } from '../lib/theme';
import type { AgencySource } from '../lib/agency-api';
import type { AgencyDay, AgencyHome, AgencyMe, AgencyOrder, AgRider, Statement } from '../lib/types';
import { Ic } from '../components/Icon';
import { Wordmark } from '../components/Wordmark';
import { LangSwitch } from '../components/Lang';
import { Loading } from '../components/Loading';
import { AccountPill, Av, MiniBars, NetText, Privacy, RiderPill, Star, WeekBars, last7 } from '../components/agences/Bits';
import { DayBoard } from '../components/agences/DayBoard';
import { AccountView } from '../components/agences/AccountView';
import { TopupPanel } from './Topup';
import { RiderEdit } from './RiderEdit';

type Tab = 'home' | 'day' | 'orders' | 'account';
const TABS: Tab[] = ['home', 'day', 'orders', 'account'];
const tabFromHash = (): Tab => { const h = location.hash.slice(1) as Tab; return TABS.includes(h) ? h : 'home'; };

function useLoad<T>(load: () => Promise<T>, deps: unknown[]) {
  const [v, setV] = useState<T | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    load().then((x) => { if (live) { setV(x); setErr(null); } }).catch((e) => { if (live) setErr(String(e?.message ?? e)); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return [v, err] as const;
}
const Err = ({ e }: { e: string }) => <div className="card empty" style={{ marginTop: 24 }}><b>{S.errors.load}</b><span className="mono">{e}</span></div>;

function Head({ me, title, sub }: { me: AgencyMe; title: string; sub: string }) {
  return <div className="ph ag-ph"><div><div className="ag-eb">{S.agency.eyebrow(me.agency.name)}</div><h1>{title}</h1><p>{sub}</p></div></div>;
}

function RiderCard({ r, onEdit }: { r: AgRider; onEdit(): void }) {
  const k = r.cash_today ? fmtN(r.cash_today) : '0';
  return (
    <div className={'card ag-rc' + (r.state !== 'active' ? ' dim' : '')}>
      <div className="ag-rch"><Av name={r.name} size={44} />
        <div className="ag-rcn"><b>{r.name}</b><span className="muted">{r.rating != null ? <><Star s={11} /> {dec1(r.rating)} · {S.agency.rc.reviews(r.reviews)}</> : r.plate ?? S.ag.file.noTrip}</span></div>
      </div>
      <div className="ag-rcnow"><RiderPill r={r} />
        {r.busy ? <span><b>{r.busy.ref}</b>{r.busy.quartier ? ` · ${r.busy.quartier}` : ''}</span>
          : r.seen_at && !r.live ? <span className="muted">{S.team.seen(rel(minutesBetween(r.seen_at)))}</span> : r.state === 'invited' ? <span className="muted">{S.agency.rc.invited}</span> : null}</div>
      <div className="ag-rcs"><div><b className="num">{r.today}</b><span>{S.agency.rc.today}</span></div><div><b className="num">{k}</b><span>{S.agency.rc.cash}</span></div><div><b className="num">{fmtN(r.trips)}</b><span>{S.agency.rc.trips}</span></div></div>
      <div className="ag-rcw"><span className="muted">{S.agency.rc.week}</span><MiniBars vals={r.week} labels={last7()} /></div>
      <div className={'ag-rcok' + (blocked(r) || r.state === 'invited' ? ' warn' : '')}>
        {blocked(r) || r.state === 'invited' ? <><Ic n="alert" s="xs" />{r.missing.length ? S.ag.t.missing(r.missing.map((x) => S.ag.docs[x]).join(', ')) : S.ag.t.consent}</> : <><Ic n="check" s="xs" />{S.agency.rc.papersOk}</>}
      </div>
      <div className="ag-rca">
        {r.phone && <a className="btn sm" href={telHref(r.phone)}><Ic n="phone" s="xs" />{S.agency.call}</a>}
        <button className="btn sm" onClick={onEdit}><Ic n="doc" s="xs" />{S.agency.editBtn}</button>
        {r.phone && <a className="ibtn" href={waHref(r.phone)} target="_blank" rel="noreferrer" aria-label={S.wa} title={S.waHint}><Ic n="msg" s="s" /></a>}
      </div>
    </div>
  );
}

function Home({ src, me, tick, reload, edit }: { src: AgencySource; me: AgencyMe; tick: number; reload(): void; edit(r: AgRider | null): void }) {
  const [h, err] = useLoad<AgencyHome>(() => src.home(), [src, tick]);
  if (err && !h) return <Err e={err} />;
  if (!h) return <Loading />;
  const full = Math.max(1, h.fee_per_day * 15);
  const perDel = h.week.delivered ? Math.round(h.week.fees / h.week.delivered) : Math.round(h.fee_per_day / 17);
  const last = h.topups.find((t) => t.status === 'completed');
  const pending = h.topups.find((t) => t.status === 'created' || t.status === 'accepted' || t.status === 'unknown') ?? null;
  const far = h.topup_before && new Date(h.topup_before).getTime() - Date.now() > 45 * 86400000;
  const live = h.riders.filter((r) => r.live), busy = live.filter((r) => r.busy).length;
  const doneBy = h.riders.filter((r) => r.today > 0).length;
  return (
    <>
      <Head me={me} title={h.name} sub={S.agency.homeSub(h.riders.length)} />
      <div className="card ag-hero">
        <div className="l">
          <div className="ag-row"><span className="cap">{S.agency.balance}</span><AccountPill a={h} /></div>
          <div className="ag-bal big num">{signedXaf(h.balance).replace(' XAF', '')}<small>XAF</small></div>
          <div className="meter ag-meter"><i className={h.balance <= 0 || h.paused ? 'bad' : (h.days_to_zero ?? 99) < 4 ? 'mid' : 'ok'} style={{ width: Math.max(2, Math.min(100, (h.balance / full) * 100)) + '%' }} /></div>
          <p className="ag-when">{h.days_to_zero != null && h.balance > 0 ? <><b>{S.agency.zeroAt(dayIn(h.days_to_zero))}</b>{S.agency.pace(xaf(h.fee_per_day), Math.round(h.fee_per_day / Math.max(1, perDel)))}</> : h.fee_per_day ? null : S.agency.noPace}</p>
          <div className="ag-rules">
            <div><Ic n="wallet" s="xs" /><span>{S.agency.rule1(h.fee_pct)}</span></div>
            <div><Ic n="clock" s="xs" /><span>{h.paused ? S.agency.pausedRule : h.balance < 0 && h.topup_before ? S.agency.rule2neg(longDay(h.topup_before)) : h.topup_before && !far ? S.agency.rule2(longDay(h.topup_before)) : S.agency.rule2far}</span></div>
            <div><Ic n="check" s="xs" /><span>{last ? <NetText text={S.agency.lastTop(xaf(last.amount), netName(last.provider), longDay(last.at))} /> : S.agency.noTop}</span></div>
          </div>
          <div className="ag-fees7"><div><span className="cap">{S.agency.fees7}</span><b className="num">{xaf(h.week.fees)}</b><span className="muted">{S.agency.for(h.week.delivered)}</span></div>
            <WeekBars vals={h.fees7} labels={last7()} w={330} h={70} fmt={fmtN} /></div>
        </div>
        <div className="r">
          <TopupPanel src={src} providers={h.topup.providers} min={h.topup.min} max={h.topup.max} feePerDelivery={perDel} balance={h.balance} phone={me.phone} pending={pending} onDone={reload} />
        </div>
      </div>
      <div className="card ag-k4">
        <div><span className="cap">{S.agency.k.delivered}</span><b className="num">{h.today.delivered}</b><p className="muted">{S.agency.k.deliveredSub(doneBy, '')}</p></div>
        <div><span className="cap">{S.agency.k.cash}</span><b className="num">{fmtN(h.today.cash)}<small>XAF</small></b><p className="muted">{S.agency.k.cashSub}</p></div>
        <div><span className="cap">{S.agency.k.fees}</span><b className="num">{fmtN(h.today.fees)}<small>XAF</small></b><p className="muted">{h.today.delivered ? S.agency.k.feesSub(xaf(Math.round(h.today.fees / h.today.delivered))) : S.agency.k.feesNone}</p></div>
        <div><span className="cap">{S.agency.k.online}</span><b className="num">{live.length}<small>/ {h.riders.filter((r) => r.state !== 'suspended').length}</small></b><p className="muted">{S.agency.k.onlineSub(busy, live.length - busy)}</p></div>
      </div>
      <div className="ag-st"><h2>{S.agency.riders(h.riders.length)}</h2><div className="r"><small className="muted">{S.agency.ridersSub}</small><button className="btn sm" onClick={() => edit(null)}><Ic n="plus" s="xs" />{S.agency.invite}</button></div></div>
      <div className="grid ag-g4">{h.riders.map((r) => <RiderCard key={r.id} r={r} onEdit={() => edit(r)} />)}</div>
      <Privacy agency={h.name} />
    </>
  );
}

function Day({ src, me, tick }: { src: AgencySource; me: AgencyMe; tick: number }) {
  const [t2, setT2] = useState(0);
  const [d, err] = useLoad<AgencyDay>(() => src.day(), [src, tick, t2]);
  const [h] = useLoad<AgencyHome>(() => src.home(), [src, tick, t2]);
  const [busy, setBusy] = useState<string | null>(null);
  if (err && !d) return <Err e={err} />;
  if (!d) return <Loading />;
  const lanes = d.agency.riders, first = firstDone(lanes), avg = avgMin(lanes);
  const ph = perHour(lanes), peak = ph.indexOf(Math.max(...ph));
  const onWay = lanes.filter((l) => l.open);
  const cash = lanes.filter((l) => laneCash(l) > 0);
  const handed = (id: string) => d.handovers.find((x) => x.rider_id === id);
  const total = cash.reduce((s, l) => s + laneCash(l), 0), left = cash.filter((l) => !handed(l.id)).reduce((s, l) => s + laneCash(l), 0);
  const tick2 = async (id: string, done: boolean) => { setBusy(id); try { await src.markHandover(id, done); setT2((x) => x + 1); } finally { setBusy(null); } };
  return (
    <>
      <Head me={me} title={S.agency.dayTitle} sub={S.agency.daySub} />
      <div className="card ag-k4">
        <div><span className="cap">{S.ag.day.delivered}</span><b className="num">{lanes.reduce((s, l) => s + l.done.length, 0)}</b><p className="muted">{first ? S.ag.day.first(hm(first)) : S.ag.day.noneYet}</p></div>
        <div><span className="cap">{S.ag.day.onWay}</span><b className="num">{onWay.length}</b><p className="muted">{onWay.map((l) => l.name.split(' ')[0]).join(', ') || '\u00a0'}</p></div>
        <div><span className="cap">{S.ag.day.rush}</span><b className="num">{ph[peak] ? `${7 + peak} h–${8 + peak} h` : '—'}</b><p className="muted">{ph[peak] ? S.ag.day.rushSub(ph[peak]) : '\u00a0'}</p></div>
        <div><span className="cap">{S.ag.day.avg}</span><b className="num">{avg != null ? <>{avg}<small>min</small></> : '—'}</b><p className="muted">{S.ag.day.avgSub}</p></div>
      </div>
      <div className="ag-side">
        <DayBoard agencies={[d.agency]} now={d.now} />
        <div className="ag-sidec">
          <div className="card ag-pad">
            <h3>{S.agency.day.toCollect}</h3><p className="muted ag-small">{S.agency.day.toCollectSub}</p>
            {cash.length ? cash.map((l) => {
              const x = handed(l.id);
              return (
                <div className="ag-cl" key={l.id}>
                  <button className={'ag-box' + (x ? ' on' : '')} aria-pressed={!!x} disabled={busy === l.id} onClick={() => tick2(l.id, !x)} aria-label={x ? S.agency.day.untick : S.agency.day.tick}>{x && <Ic n="check" s="xs" />}</button>
                  <div><b>{l.name}</b><small className="muted">{S.ag.day.nDone(l.done.length)} · {x ? S.agency.day.handed(hm(x.at)) : S.agency.day.toHand}</small></div>
                  <b className="num">{fmtN(laneCash(l))}</b>
                </div>
              );
            }) : <p className="muted ag-small">{S.agency.day.nothing}</p>}
            {cash.length > 0 && <div className="ag-cl tot"><div><b>{S.agency.day.left}</b><small className="muted">{S.agency.day.leftSub(xaf(total))}</small></div><b className="num">{xaf(left)}</b></div>}
          </div>
          {h && (
            <div className="card ag-pad ag-wmini">
              <div className="ag-row"><span className="cap">{S.agency.balance}</span><AccountPill a={h} /></div>
              <div className="ag-bal num">{signedXaf(h.balance).replace(' XAF', '')}<small>XAF</small></div>
              <p className="muted ag-small">{h.topup_before && new Date(h.topup_before).getTime() - Date.now() < 45 * 86400000 ? S.agency.rule2(longDay(h.topup_before)) : S.agency.rule2far}</p>
              <a className="btn sm pri" href="#account">{S.agency.tu.title}</a>
            </div>
          )}
        </div>
      </div>
      <Privacy agency={me.agency.name} />
    </>
  );
}

function Orders({ src, me, tick }: { src: AgencySource; me: AgencyMe; tick: number }) {
  const [open, e1] = useLoad<AgencyOrder[]>(() => src.orders('open'), [src, tick]);
  const [ended, e2] = useLoad<AgencyOrder[]>(() => src.orders('ended'), [src, tick]);
  const [h] = useLoad<AgencyHome>(() => src.home(), [src, tick]);
  const riderName = (id: string | null) => (id ? h?.riders.find((r) => r.id === id)?.name ?? '—' : '—');
  if ((e1 && !open) || (e2 && !ended)) return <Err e={(e1 ?? e2)!} />;
  if (!open || !ended) return <Loading />;
  const table = (rows: AgencyOrder[]) => (
    <div className="card"><div className="tblwrap"><table className="tbl ag-ord">
      <thead><tr><th>{S.agency.ord.h.ref}</th><th>{S.agency.ord.h.status}</th><th>{S.agency.ord.h.quartier}</th><th>{S.agency.ord.h.rider}</th><th>{S.agency.ord.h.at}</th><th className="r">{S.agency.ord.h.fare}</th></tr></thead>
      <tbody>
        {rows.map((o) => (
          <tr key={o.id} className="nohover"><td><b className="num">{o.ref}</b><small className="muted"> · {o.order_type === 'first_install' ? S.agency.ord.install : S.agency.ord.refill}</small></td>
            <td><span className={'st ' + (o.status === 'completed' || o.status === 'delivered' ? 'green' : o.status.startsWith('cancelled') || o.status === 'failed_first_install' ? 'grey' : o.status === 'placed' ? 'amber' : 'blue')}>{S.agency.ord.status[o.status] ?? o.status}</span></td>
            <td>{o.customer.quartier ?? '—'}</td><td>{riderName(o.rider_id)}</td>
            <td className="muted">{dayLabel(o.created_at, { weekday: 'short', day: 'numeric', month: 'short' })} · {hhmm(o.created_at)}</td>
            <td className="r num">{o.fare != null ? xaf(o.fare) : '—'}</td></tr>
        ))}
        {!rows.length && <tr><td colSpan={6}><div className="empty">{S.agency.ord.none}</div></td></tr>}
      </tbody>
    </table></div></div>
  );
  return (
    <>
      <Head me={me} title={S.agency.ordersTitle} sub={S.agency.ordersSub} />
      <div className="ag-st"><h2>{S.agency.ord.open} · {open.length}</h2></div>{table(open)}
      <div className="ag-st"><h2>{S.agency.ord.ended} · {ended.length}</h2></div>{table(ended)}
      <Privacy agency={me.agency.name} />
    </>
  );
}

function Account({ src, me, tick, reload }: { src: AgencySource; me: AgencyMe; tick: number; reload(): void }) {
  const [st, err] = useLoad<Statement>(() => src.statement(), [src, tick]);
  const [h, err2] = useLoad<AgencyHome>(() => src.home(), [src, tick]);
  if ((err && !st) || (err2 && !h)) return <Err e={(err ?? err2)!} />;
  if (!st || !h) return <Loading />;
  const perDel = h.week.delivered ? Math.round(h.week.fees / h.week.delivered) : Math.round(h.fee_per_day / 17);
  const pending = h.topups.find((t) => t.status === 'created' || t.status === 'accepted' || t.status === 'unknown') ?? null;
  return (
    <>
      <Head me={me} title={S.agency.accTitle} sub={S.agency.accSub} />
      <AccountView st={st} acc={{ ...h, name: h.name, topup_before: h.topup_before }} updatedAt={new Date(h.generated_at)}
        side={<div className="card ag-tucard"><TopupPanel src={src} providers={h.topup.providers} min={h.topup.min} max={h.topup.max} feePerDelivery={perDel} balance={h.balance} phone={me.phone} pending={pending} onDone={reload} /></div>} />
      <Privacy agency={h.name} />
    </>
  );
}

export function AgencyApp({ src, me, onSignOut, onLeaveDemo }: { src: AgencySource; me: AgencyMe; onSignOut?(): void; onLeaveDemo?(): void }) {
  const [tab, setTab] = useState<Tab>(tabFromHash);
  const [theme, setTheme] = useState<Theme>(readTheme);
  const [tick, setTick] = useState(0);
  const [editing, setEditing] = useState<AgRider | null | false>(false);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => { applyTheme(theme); }, [theme]);
  useEffect(() => {
    const onHash = () => setTab(tabFromHash());
    addEventListener('hashchange', onHash);
    return () => removeEventListener('hashchange', onHash);
  }, []);
  // The figures move during the day: read again every minute while the page is in front.
  useEffect(() => {
    const t = setInterval(() => { if (document.visibilityState === 'visible') reload(); }, 60000);
    return () => clearInterval(t);
  }, [reload]);
  const go = (t: Tab) => { setTab(t); try { history.replaceState(null, '', location.search + '#' + t); } catch { /* sandboxed */ } window.scrollTo({ top: 0 }); };
  const pane = tab === 'home' ? <Home src={src} me={me} tick={tick} reload={reload} edit={(r) => setEditing(r)} />
    : tab === 'day' ? <Day src={src} me={me} tick={tick} />
      : tab === 'orders' ? <Orders src={src} me={me} tick={tick} />
        : <Account src={src} me={me} tick={tick} reload={reload} />;
  return (
    <>
      <header className="top">
        <div className="bar">
          <div className="brand"><img src="./mygaz-icon.png" alt="" /><Wordmark /><span className="tag">{S.agency.space}</span></div>
          <nav className="tabs" aria-label="Sections">
            {TABS.map((k) => <button key={k} className="tab" aria-current={tab === k ? 'page' : undefined} onClick={() => go(k)}>{S.agency.tabs[k]}</button>)}
          </nav>
          <div className="acts">
            <span className="ag-agn hide-s">{me.agency.name}</span>
            <button className="ibtn hide-s" aria-label={`${S.theme} : ${S.themeNames[theme]}`} onClick={() => { const t = THEMES[(THEMES.indexOf(theme) + 1) % 3]; setTheme(t); saveTheme(t); }}><Ic n={theme === 'light' ? 'sun' : theme === 'dark' ? 'moon' : 'auto'} s="s" /></button>
            <span className="avatar dark" title={me.name}>{me.agency.name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase()}</span>
          </div>
        </div>
      </header>
      <main>
        {src.demo && <div className="demo-bar"><span className="st violet">{S.demoPill}</span>{onLeaveDemo && <button className="link" onClick={onLeaveDemo}>{S.demoLeave}</button>}</div>}
        <section className="view on ag" key={tab}>{pane}</section>
        <div className="ops-foot">{onSignOut && <button className="link" onClick={onSignOut}><Ic n="undo" s="xs" />{S.login.signOut}</button>}<LangSwitch /></div>
      </main>
      {editing !== false && <RiderEdit src={src} agencyId={me.agency.id} rider={editing} onClose={() => setEditing(false)} onSaved={reload} />}
    </>
  );
}
